import React, { useEffect, useState, useMemo } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import Appointment from "../../../Models/Appointment/Appointment";
import { CustomMainButton } from "../../common/components/CustomMainButton";
import { RequestLayout } from "../../common/components/RequestLayout";
import { sortServices, stepLayoutItemsId } from "../../../Utils";
import { StepInfo } from "../../common/components/StepInfo";
import { CommonLayout } from "../../common/components/CommonLayout";
import { useTranslation } from "react-i18next";
import { css } from "aphrodite";
import styles from "./styles";
import { RoutePath } from "../../../Utils/routePath";
import { Media } from "../../../Models";
import { CustomMobileServiceButton } from "../../common/components/CustomMobileServiceButton";

const SelectSubServicePage = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation(["translation"]);
    const stepItems = useSelector(Appointment.getStepItems);
    const isLoading = useSelector(Appointment.getServicesLoading);
    const servicesData = useSelector(Appointment.getServicesData);
    const error = useSelector(Appointment.getServicesError);
    const selectedServiceId = useSelector(Appointment.getSelectedServiceId);
    const selectedBranchId = useSelector(Appointment.getSelectedBranchId);
    const waysOfActionIsLoading = useSelector(Appointment.getWaysOfActionLoading);
    const waysOfActionError = useSelector(Appointment.getWaysOfActionError);
    const waysOfActionData = useSelector(Appointment.getWaysOfActionData);
    const selectedServiceGroupId = useSelector(Appointment.getSelectedServiceGroupId);
    const selectedSubServiceGroupId = useSelector(Appointment.getSelectedSubServiceGroupId);
    const webView = useSelector(Media.getIsWebView);
    const [triggerGoToNextStep, setTriggerGoToNextStep] = useState(false);
    const currentStep = stepLayoutItemsId.selectSubService;

    useEffect(() => {
        if (!selectedSubServiceGroupId) {
            navigate(RoutePath.selectServicePage, { replace: true });
        }
    }, [selectedSubServiceGroupId, navigate]);

    const services = useMemo(() => {
        if (selectedSubServiceGroupId && Array.isArray(servicesData)) {
            const individualServices = servicesData
                .filter(s => !s.tags?.ServiceGroupId && !s.tags?.SubServiceId && s.tags?.SubServiceGroupId === selectedSubServiceGroupId)
                .sort(sortServices);

            const serviceGroups = Object.groupBy(
                servicesData.filter(s => !!s.tags?.ServiceGroupId && s.tags?.SubServiceGroupId === selectedSubServiceGroupId),
                (s) => s.tags?.ServiceGroupId
            );

            const newServices = [
                ...individualServices,
                ...Object.keys(serviceGroups).map(key => serviceGroups[key][0]),
            ].sort(sortServices);

            return newServices;
        }
        return [];
    }, [selectedSubServiceGroupId, servicesData]);

    useEffect(() => {
        if (triggerGoToNextStep) {
            if (selectedServiceGroupId) {
                Appointment.stepCompleted(currentStep);
                const currentStepIndex = stepItems.findIndex((step) => step.id === currentStep);
                let nextStep = stepItems[currentStepIndex + 1];
                setTriggerGoToNextStep(false);
                if (Array.isArray(waysOfActionData?.availableChannels)) {
                    Appointment.setSelectedChannel(waysOfActionData.availableChannels[0]);
                }
                navigate(nextStep.path);
            } else if (waysOfActionData?.availableChannels) {
                Appointment.setSelectedChannel(waysOfActionData?.availableChannels[0]);
                Appointment.stepCompleted(currentStep);
                const currentStepIndex = stepItems.findIndex((step) => step.id === currentStep);
                let nextStep = stepItems[currentStepIndex + 1];
                setTriggerGoToNextStep(false);
                navigate(nextStep.path);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerGoToNextStep]);

    const handleOnClickService = async (serviceId, eventTypeId, serviceName, serviceDescription) => {
        Appointment.setSelectedService(serviceId, eventTypeId, serviceName, serviceDescription);
        const waysOfActionResponse = await Appointment.fetchWaysOfAction(eventTypeId);
        if (Array.isArray(waysOfActionResponse?.data?.availableChannels)) {
            Appointment.setSelectedChannel(waysOfActionResponse.data.availableChannels[0]);
        }
        setTriggerGoToNextStep(true);
    };

    const handleOnClickServiceGroup = async (serviceGroupId) => {
        if (selectedBranchId) {
            const service = servicesData.find(s => s.tags?.ServiceGroupId === serviceGroupId);
            if (service) {
                Appointment.setSelectedServiceGroupId(serviceGroupId);
                Appointment.setSelectedService(service.id, service.eventTypeId, service.name, service.description);
                const waysOfActionResponse = await Appointment.fetchWaysOfAction(service.eventTypeId);
                if (Array.isArray(waysOfActionResponse?.data?.availableChannels)) {
                    Appointment.setSelectedChannel(waysOfActionResponse.data.availableChannels[0]);
                }
                setTriggerGoToNextStep(true);
            }
        } else {
            Appointment.setSelectedServiceGroupId(serviceGroupId);
            setTriggerGoToNextStep(true);
        }
    };

    return (
        <>
            <CommonLayout mainLayout={false}>
                <StepInfo
                    activeStepId={currentStep}
                    layoutItems={stepItems}
                    webView={webView}
                />
            </CommonLayout>
            <RequestLayout
                loading={isLoading || waysOfActionIsLoading || (triggerGoToNextStep && !waysOfActionError)}
                error={error || waysOfActionError}
            >
                {services.length > 0 && (
                    <CommonLayout mainLayout={false}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Grid container spacing={2} className={css(styles.gridDiv)}>
                                {services.map((item) => {
                                    if (!item.tags?.ServiceGroupId && item.users?.length > 0) {
                                        return (
                                            <Grid className={css(styles.insideGrid)} key={item.id}>
                                                {webView ? (
                                                    <CustomMainButton
                                                        isSelected={item.id === selectedServiceId}
                                                        textVariant="subtitle1"
                                                        value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                        onClick={() => handleOnClickService(
                                                            item.id,
                                                            item.eventTypeId,
                                                            item.name,
                                                            item.description
                                                        )}
                                                    />
                                                ) : (
                                                    <CustomMobileServiceButton
                                                        isSelected={item.id === selectedServiceId}
                                                        value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                        onClickButton={() => handleOnClickService(
                                                            item.id,
                                                            item.eventTypeId,
                                                            item.name,
                                                            item.description
                                                        )}
                                                    />
                                                )}
                                            </Grid>
                                        );
                                    }
                                    else if (!!item.tags?.ServiceGroupId) {
                                        return (
                                            <Grid className={css(styles.insideGrid)} key={item.id}>
                                                {webView ? (
                                                    <CustomMainButton
                                                        isSelected={selectedServiceGroupId === item.tags.ServiceGroupId}
                                                        textVariant="subtitle1"
                                                        value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                        onClick={() => handleOnClickServiceGroup(item.tags.ServiceGroupId)}
                                                    />
                                                ) : (
                                                    <CustomMobileServiceButton
                                                        isSelected={selectedServiceGroupId === item.tags.ServiceGroupId}
                                                        value={item?.['name_' + i18n.language] || item?.name || "-"}
                                                        onClickButton={() => handleOnClickServiceGroup(item.tags.ServiceGroupId)}
                                                    />
                                                )}
                                            </Grid>
                                        );
                                    }
                                    return null;
                                })}
                            </Grid>
                        </Box>
                    </CommonLayout>
                )}
            </RequestLayout>
        </>
    );
};

export { SelectSubServicePage };
